import { Flex, Table, Text } from "@mantine/core";
import { Circle } from "@mui/icons-material";
import { SvgIcon } from "@mui/material";
import {
    ContactInfo,
    DaysAvailable,
    ImageWithPlaceholder,
    UserRoleEnum
} from "@therapprove/common";
import { FC } from "react";
import { LocationMenu } from "./LocationMenu";
import { TeamActionsMenu } from "./TeamActionsMenu";

// Constants
const ROLE_DISPLAY: Record<string, string> = {
    [UserRoleEnum.OrganizationOwner]: "Owner",
    [UserRoleEnum.OrganizationAdmin]: "Admin",
    [UserRoleEnum.CaseManager]: "Coordinator",
    [UserRoleEnum.Provider]: "Provider"
};

type TeamMemberLocation = {
    locationId: string;
    name: string;
    address1?: string | null;
    city?: string | null;
    stateId?: string | null;
};

type TeamRowProps = {
    userId: string;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    imageUrl?: string | null;
    isActive: boolean;
    roleName?: string | null;
    daysAvailable?: string[] | null;
    locations?: TeamMemberLocation[] | null;
    onClick: () => void;
};

export const TeamRow: FC<TeamRowProps> = ({
    userId,
    firstName,
    lastName,
    email,
    phoneNumber,
    imageUrl,
    isActive,
    roleName,
    daysAvailable,
    locations,
    onClick
}) => {
    // Data
    const roleLabel = roleName ? ROLE_DISPLAY[roleName] ?? roleName : "";

    return (
        <Table.Tr
            style={{ cursor: "pointer" }}
            onClick={onClick}
            onDoubleClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
            }}
        >
            {/* Status indicator */}
            <Table.Td w={20}>
                {!isActive && (
                    <SvgIcon className="inactiveIndicator" component={Circle} />
                )}
            </Table.Td>

            {/* Name + role */}
            <Table.Td>
                <Flex gap="sm" align="center">
                    <ImageWithPlaceholder
                        imageUrl={imageUrl}
                        avatarText={`${firstName} ${lastName}`}
                        size={35}
                    />
                    <Flex direction="column">
                        <Text variant="body">{`${lastName}, ${firstName}`}</Text>
                        <Text variant="meta" c="dimmed">
                            {roleLabel}
                        </Text>
                    </Flex>
                </Flex>
            </Table.Td>

            {/* Contact */}
            <Table.Td>
                <ContactInfo email={email} phoneNumber={phoneNumber} />
            </Table.Td>

            {/* Availability */}
            <Table.Td>
                <DaysAvailable daysAvailable={daysAvailable} />
            </Table.Td>

            {/* Location */}
            <Table.Td>
                <LocationMenu locations={locations ?? []} />
            </Table.Td>

            {/* Actions */}
            <Table.Td>
                <TeamActionsMenu
                    userId={userId}
                    email={email}
                    name={`${firstName} ${lastName}`}
                    isActive={isActive}
                />
            </Table.Td>
        </Table.Tr>
    );
};
